import { Icon, IconButton } from "@appretio/ui-kit";
import { useSyncExternalStore } from "react";
import { useFeedbackApi } from "../../FeedbackContext";
import {
  CELL_KEY,
  FEEDBACK_VIEW_KEY,
  type FeedbackViewRestore,
  GRID_VIEW_KEY,
  TOP_BAR_KEY
} from "../../feedbackViewRestore";
import { RESTORE_BANNER_ATTRIBUTE } from "../../userInput";
import {
  bannerBase,
  bannerBody,
  bannerNotes,
  bannerTitle
} from "./FeedbackRestoreBanner.css";

export interface FeedbackRestoreBannerProps {
  /** The parsed feedback restore the banner summarises. */
  restore: FeedbackViewRestore;
  /**
   * What the page could not set back from this report, in the page's own
   * words (e.g. "Unsaved edits to the buy are not restored."). Shown after
   * the banner's own notes.
   */
  notes?: string[];
  /** Why the restore did not load the reporter's data, when it did not. */
  applyNote?: string | null;
  /** Called when the user dismisses the banner. */
  onDismiss: () => void;
}

/** How the banner names a context block the size budget dropped. */
function droppedBlockLabel(block: string): string {
  if (block === TOP_BAR_KEY) return "the context selection";
  if (block === FEEDBACK_VIEW_KEY) return "the page's view state";
  if (block === CELL_KEY) return "the reported cell";
  if (block === GRID_VIEW_KEY) return "the grid's columns and filters";
  return block;
}

/** An instant in the reader's own timezone. */
function formatInstant(instant: string): string {
  return new Date(instant).toLocaleString("en-GB");
}

const NO_NOTES: string[] = [];

function noSubscription() {
  return () => {};
}

function noNotes() {
  return NO_NOTES;
}

/**
 * What the restore could not put back on the page's keyed grids. Empty
 * outside a FeedbackProvider.
 */
function useGridViewNotes(): string[] {
  const gridViews = useFeedbackApi()?.gridViews;
  return useSyncExternalStore(
    gridViews?.subscribe ?? noSubscription,
    gridViews?.notes ?? noNotes
  );
}

/** Dismissible banner shown after a feedback view restore. */
export function FeedbackRestoreBanner({
  restore,
  notes = [],
  applyNote,
  onDismiss
}: FeedbackRestoreBannerProps) {
  const { record, cell, droppedBlocks } = restore;
  const gridNotes = useGridViewNotes();
  const hasSelection = Object.keys(restore.selectorDraft ?? {}).length > 0;
  const hasGrids = Object.keys(restore.grids ?? {}).length > 0;
  const restored = [
    ...(hasSelection ? ["the context selection"] : []),
    ...(hasGrids ? ["the grid's columns and filters"] : [])
  ].join(" and ");
  // "columns and filters" is plural, whatever goes with it.
  const restoredVerb = hasGrids ? "are" : "is";
  const viewDropped = droppedBlocks.includes(FEEDBACK_VIEW_KEY);
  const shownNotes: string[] = [];
  if (droppedBlocks.length > 0) {
    const named = droppedBlocks.map(droppedBlockLabel).join(", ");
    let outcome = "";
    if (viewDropped) {
      outcome = restored
        ? ` Only ${restored} ${restoredVerb} restored.`
        : " Showing the page with its defaults.";
    }
    shownNotes.push(
      `This report was over the size budget, so it did not capture ${named}.${outcome}`
    );
  }
  if (!viewDropped && !restore.view) {
    shownNotes.push(
      restored
        ? `This report predates full view capture — only ${restored} ${restoredVerb} restored; everything else is at the page's defaults.`
        : "This report carries no restorable view state — showing the page with its defaults."
    );
  }
  if (applyNote) shownNotes.push(applyNote);
  if (cell) {
    shownNotes.push(
      `Reported cell: ${cell.header ?? cell.field} = ${String(cell.value ?? "—")}`
    );
  }
  shownNotes.push(...notes, ...gridNotes);
  if (record.screenshot_count > 0) {
    shownNotes.push(
      `${record.screenshot_count} screenshot(s) on the feedback row show exactly what the reporter saw.`
    );
  }

  return (
    <div
      className={bannerBase}
      role="status"
      {...{ [RESTORE_BANNER_ATTRIBUTE]: "" }}
    >
      <Icon name="info" />
      <div className={bannerBody}>
        <p className={bannerTitle}>
          Viewing the state {record.created_by} reported
          {restore.reportedAt ? ` on ${formatInstant(restore.reportedAt)}` : ""}
          . Data shown is current.
        </p>
        {shownNotes.length > 0 && (
          <ul className={bannerNotes}>
            {[...new Set(shownNotes)].map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>
      <IconButton
        variant="tertiary"
        size="xs"
        iconName="close"
        aria-label="Dismiss restore banner"
        onClick={onDismiss}
      />
    </div>
  );
}
